import { AccountAddress } from '../../../../shared/types/modules/account';
import {
    CreateOrbitCasePayload,
    OrbitCatalogEnum,
    OrbitPreferredContactMethodEnum,
} from '../../../../shared/types/orbit';
import {
    asyncGetCountries,
    asyncGetCountry,
    asyncGetCountryByCode,
} from '../../../../services/location.service';
import {
    asyncGetOrbitCatalog,
    asyncGetOrbitCitiesFlat,
} from '../../../../services/orbit.service';
import { ISelect, ParamEnum } from '../../../../shared/types/shared';
import { asyncGetParams } from '../../../../services/param.service';
import { cleanObj } from '../../../../shared/utils/objects';
import { resolveCrmFormTypeToOrbitChannel } from '../../../../shared/utils/orbitChannelMap';
import {
    getOrbitChannelConfig,
    isOrbitDefensorConsumidor,
    OrbitChannelFormValues,
} from '../channelForm/formData';
import { OrbitCallInfoValues } from '../callInfo/formData';
import { OrbitCitizenInfoValues } from '../citizenInfo/formData';
import { OrbitPqrInfoValues } from '../pqrInfo/formData';
import { OrbitPrivacyConsentValues } from '../privacyConsent/formData';
import { OrbitSpecialConditionValues } from '../specialCondition/formData';
import { getOptionName } from './formatOrbitSelect';

type BuildOrbitCasePayloadInput = {
    callInfo: OrbitCallInfoValues;
    channel: OrbitChannelFormValues;
    citizen: OrbitCitizenInfoValues;
    address: AccountAddress;
    specialCondition: OrbitSpecialConditionValues;
    pqrInfo: OrbitPqrInfoValues;
    privacyConsent: OrbitPrivacyConsentValues;
};

const buildCitizenName = (citizen: OrbitCitizenInfoValues) =>
    [citizen.name, citizen.lastName].filter(Boolean).join(' ').trim();

const resolveCitizenGender = (gender?: string): number | undefined => {
    const trimmed = gender?.trim();

    if (!trimmed) return undefined;

    const asNumber = Number(trimmed);

    if (Number.isInteger(asNumber) && asNumber > 0) {
        return asNumber;
    }

    const genderMap: Record<string, number> = {
        FEMENINO: 1,
        MASCULINO: 2,
        NO_BINARIO: 4,
        NA: 10,
    };

    return genderMap[trimmed.toUpperCase()];
};

const COLOMBIA_COUNTRY_CODE = '170';
const COLOMBIA_ISO_CODE = 'CO';

const ORBIT_ISO_TO_CRM_COUNTRY_CODE: Record<string, string> = {
    [COLOMBIA_ISO_CODE]: COLOMBIA_COUNTRY_CODE,
};

const isNumericCountryCode = (value: string) => /^\d+$/.test(value);
const isIsoCountryCode = (value: string) => /^[A-Za-z]{2}$/.test(value);

const resolveCrmCountry = async (
    orbitCountries: ISelect[],
    countryValue?: string
): Promise<ISelect | undefined> => {
    const trimmed = countryValue?.trim();

    if (!trimmed) return undefined;

    const selectedOrbitCountry = orbitCountries.find(
        (country) =>
            String(country.id) === trimmed ||
            String(country.code ?? '') === trimmed ||
            country.name.localeCompare(trimmed, 'es', {
                sensitivity: 'base',
            }) === 0
    );

    const isoCode = String(
        selectedOrbitCountry?.code ?? (isIsoCountryCode(trimmed) ? trimmed : '')
    ).toUpperCase();

    const numericCodesToTry = [
        ORBIT_ISO_TO_CRM_COUNTRY_CODE[isoCode],
        isNumericCountryCode(trimmed) ? trimmed : '',
        selectedOrbitCountry &&
        isNumericCountryCode(String(selectedOrbitCountry.id))
            ? String(selectedOrbitCountry.id)
            : '',
        selectedOrbitCountry?.code &&
        isNumericCountryCode(String(selectedOrbitCountry.code))
            ? String(selectedOrbitCountry.code)
            : '',
    ].filter(
        (code, index, list) => Boolean(code) && list.indexOf(code) === index
    );

    for (const code of numericCodesToTry) {
        const country = await asyncGetCountryByCode(code);
        if (country?.id) return country;
    }

    const countryById = await asyncGetCountry(trimmed);
    if (countryById?.id) return countryById;

    const countryName =
        selectedOrbitCountry?.name?.trim() ||
        getOptionName(orbitCountries, trimmed) ||
        trimmed;

    const crmCountries = await asyncGetCountries();

    return crmCountries.find(
        (country) =>
            String(country.id) === trimmed ||
            (numericCodesToTry.length > 0 &&
                numericCodesToTry.includes(String(country.code ?? ''))) ||
            country.name.localeCompare(countryName, 'es', {
                sensitivity: 'base',
            }) === 0
    );
};

const resolveDepartmentCodeFromCity = (
    cityCode?: string
): string | undefined => {
    const trimmed = cityCode?.trim();

    if (!trimmed || !/^\d{5}$/.test(trimmed)) return undefined;

    return trimmed.slice(0, 2);
};

const resolveDefaultCatalogId = async (catalog: OrbitCatalogEnum) => {
    const { items, error } = await asyncGetOrbitCatalog(catalog);

    if (error) {
        throw new Error(error);
    }

    if (!items[0]?.id) {
        throw new Error(
            `No se encontraron opciones en el catálogo ${catalog}.`
        );
    }

    return String(items[0].id);
};

const buildMetadata = (
    formType: string | undefined,
    privacyConsent: OrbitPrivacyConsentValues,
    attachmentsCount: number
) => {
    const channelConfig = getOrbitChannelConfig(formType);

    return cleanObj({
        source_system: 'crmware',
        accept_terms: privacyConsent.acceptTerms,
        form_type: formType,
        form_embed_code: channelConfig?.embedCode,
        form_id: channelConfig?.formId,
        attachments_count: attachmentsCount > 0 ? attachmentsCount : undefined,
    });
};

const buildPqrContext = (
    channel: OrbitChannelFormValues,
    citizen: OrbitCitizenInfoValues,
    specialCondition: OrbitSpecialConditionValues,
    pqrInfo: OrbitPqrInfoValues,
    callInfo: OrbitCallInfoValues
) =>
    cleanObj({
        callInfo: callInfo.callId?.trim()
            ? { callId: callInfo.callId.trim() }
            : undefined,
        personType: citizen.personType,
        typeDocument: citizen.typeDocument,
        document: citizen.document,
        name: citizen.name.trim(),
        lastName: citizen.lastName?.trim() || undefined,
        email: citizen.email?.trim() || undefined,
        alternateEmail: citizen.alternateEmail?.trim() || undefined,
        cellPhone: citizen.cellPhone?.trim() || undefined,
        phone: citizen.phone?.trim() || undefined,
        gender: citizen.gender || undefined,
        isLGBTIQ: citizen.isLGBTIQ,
        country: pqrInfo.country,
        location: pqrInfo.city || undefined,
        detail: pqrInfo.detail.trim(),
        caseType: pqrInfo.caseType,
        formType: channel.orbitChannel || undefined,
        admission: isOrbitDefensorConsumidor(channel.orbitChannel)
            ? channel.sfcAdmission
            : undefined,
        correspondenceFiling: isOrbitDefensorConsumidor(channel.orbitChannel)
            ? channel.correspondenceFiling.trim()
            : undefined,
        specialCondition: specialCondition.hasSpecialCondition
            ? {
                hasSpecialCondition: true,
                specialCondition: specialCondition.specialCondition,
                observations: specialCondition.observations.trim(),
            }
            : undefined,
        attachments:
            pqrInfo.attachments?.length > 0 ? pqrInfo.attachments : undefined,
    });

const buildCustomFields = (
    citizen: OrbitCitizenInfoValues,
    specialCondition: OrbitSpecialConditionValues,
    channel: OrbitChannelFormValues,
    admissionName?: string
) => {
    const isDefensor = isOrbitDefensorConsumidor(channel.orbitChannel);

    return cleanObj({
        person_type: citizen.personType || null,
        is_lgbtiq: citizen.isLGBTIQ,
        has_special_condition: specialCondition.hasSpecialCondition,
        special_condition: specialCondition.hasSpecialCondition
            ? specialCondition.specialCondition
            : null,
        special_condition_observations: specialCondition.hasSpecialCondition
            ? specialCondition.observations.trim()
            : null,
        admision: isDefensor ? admissionName : null,
        radicado_correspondencia: isDefensor
            ? channel.correspondenceFiling.trim()
            : null,
    });
};

export const buildOrbitCasePayload = async ({
    callInfo,
    channel,
    citizen,
    address,
    specialCondition,
    pqrInfo,
    privacyConsent,
}: BuildOrbitCasePayloadInput): Promise<CreateOrbitCasePayload> => {
    const [
        categoryId,
        priorityId,
        departmentsCatalog,
        eventCitiesResult,
        addressCitiesResult,
        countriesResult,
    ] = await Promise.all([
        resolveDefaultCatalogId(OrbitCatalogEnum.Categories),
        resolveDefaultCatalogId(OrbitCatalogEnum.Priorities),
        asyncGetOrbitCatalog(OrbitCatalogEnum.Departments),
        pqrInfo.department.trim()
            ? asyncGetOrbitCatalog(OrbitCatalogEnum.Cities, {
                department_code: pqrInfo.department.trim(),
            })
            : Promise.resolve({ items: [] as ISelect[], error: undefined }),
        asyncGetOrbitCitiesFlat(),
        asyncGetOrbitCatalog(OrbitCatalogEnum.Countries),
    ]);

    const catalogError =
        departmentsCatalog.error ||
        eventCitiesResult.error ||
        addressCitiesResult.error ||
        countriesResult.error;

    if (catalogError) {
        throw new Error(catalogError);
    }

    const countries = countriesResult.items;

    const addressCityCode = (address.city as string)?.trim() || undefined;
    const eventDepartmentCode = pqrInfo.department.trim() || undefined;
    const eventCityCode = pqrInfo.city.trim() || undefined;
    const countryName =
        getOptionName(countries, pqrInfo.country) ?? pqrInfo.country;
    const crmCountry = await resolveCrmCountry(countries, pqrInfo.country);

    if (!crmCountry?.id) {
        throw new Error('No se pudo resolver el país en el CRM.');
    }

    const orbitCaseType = pqrInfo.caseType?.trim();

    if (!orbitCaseType) {
        throw new Error('El tipo de caso PQRS es obligatorio.');
    }

    const citizenCityCode = addressCityCode || undefined;
    const citizenDepartmentCode =
        resolveDepartmentCodeFromCity(citizenCityCode) || undefined;
    const detail = pqrInfo.detail.trim();
    const isDefensor = isOrbitDefensorConsumidor(channel.orbitChannel);
    const admissionName = isDefensor
        ? getOptionName(
            await asyncGetParams(ParamEnum.PQR_ADMISSION),
            channel.sfcAdmission
        )
        : undefined;
    const customFields = buildCustomFields(
        citizen,
        specialCondition,
        channel,
        admissionName
    );
    const attachmentsCount = pqrInfo.attachments?.length ?? 0;
    const orbitChannel = resolveCrmFormTypeToOrbitChannel(channel.orbitChannel);

    if (!orbitChannel) {
        throw new Error(
            'No se pudo resolver el canal de Orbit para el formulario seleccionado.'
        );
    }

    const payload = cleanObj({
        case_type: orbitCaseType,
        subject: detail.slice(0, 500),
        description: detail,
        category_id: categoryId,
        priority_id: priorityId,
        citizen_document: citizen.document,
        citizen_name: buildCitizenName(citizen),
        citizen_first_name: citizen.name?.trim() || undefined,
        citizen_last_name: citizen.lastName?.trim() || undefined,
        citizen_document_type: citizen.typeDocument,
        citizen_gender: resolveCitizenGender(citizen.gender),
        citizen_email: citizen.email?.trim() || undefined,
        citizen_alternative_email: citizen.alternateEmail?.trim() || undefined,
        citizen_phone:
            citizen.phone?.trim() || citizen.cellPhone?.trim() || undefined,
        citizen_address: address.address?.trim() || undefined,
        citizen_city: citizenCityCode,
        citizen_department: citizenDepartmentCode,
        citizen_country: countryName || undefined,
        event_department: eventDepartmentCode,
        event_city: eventCityCode,
        preferred_contact_method:
            pqrInfo.preferredContactMethod ||
            OrbitPreferredContactMethodEnum.Email,
        channel: orbitChannel,
        orbit_channel: channel.orbitChannel || undefined,
        metadata: buildMetadata(
            channel.orbitChannel,
            privacyConsent,
            attachmentsCount
        ),
        custom_fields:
            customFields && Object.keys(customFields).length > 0
                ? customFields
                : undefined,
        pqr: buildPqrContext(
            channel,
            citizen,
            specialCondition,
            { ...pqrInfo, country: String(crmCountry.id) },
            callInfo
        ),
    }) as CreateOrbitCasePayload;

    return {
        ...payload,
        habeas_data_accepted: privacyConsent.acceptTerms === true,
    };
};
